import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { Pencil, Trash2, Search, X, Download, FileSpreadsheet, Check } from 'lucide-react';
import api from '../services/api';
import { PageHeader, TableSkeleton, EmptyState, StatusBadge, Pagination } from '../components/PageParts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { MASTER_CONFIGS } from '../config/mastersConfig';

function renderCell(item, col) {
  const v = item[col.key];
  if (col.type === 'status') {
    return <StatusBadge status={v === 'active' ? 'Ativo' : 'Inativo'} />;
  }
  if (col.type === 'boolean') {
    return v ? <Check className="h-4 w-4 text-emerald-600" /> : <span className="text-neutral-400">-</span>;
  }
  if (col.type === 'color') {
    return v ? (
      <div className="flex items-center gap-2">
        <span className="inline-block h-4 w-4 rounded" style={{ background: v }} />
        <span className="font-mono text-xs">{v}</span>
      </div>
    ) : <span className="text-neutral-400">-</span>;
  }
  if (col.mono) return <span className="font-mono text-xs">{v || '-'}</span>;
  return v || <span className="text-neutral-400">-</span>;
}

function FieldRenderer({ field, value, onChange }) {
  const cs = field.colSpan ? `md:col-span-${field.colSpan}` : '';
  if (field.type === 'textarea') {
    return (
      <div className={`space-y-1.5 ${cs}`}>
        <Label>{field.label}{field.required && ' *'}</Label>
        <Textarea rows={2} required={field.required} value={value || ''} onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }
  if (field.type === 'select') {
    return (
      <div className={`space-y-1.5 ${cs}`}>
        <Label>{field.label}{field.required && ' *'}</Label>
        <Select value={value || ''} onValueChange={onChange}>
          <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
          <SelectContent>{field.options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    );
  }
  if (field.type === 'switch') {
    return (
      <div className={`flex items-center gap-3 pt-6 ${cs}`}>
        <Switch checked={!!value} onCheckedChange={onChange} />
        <span className="text-sm">{field.label}</span>
      </div>
    );
  }
  if (field.type === 'color') {
    return (
      <div className={`space-y-1.5 ${cs}`}>
        <Label>{field.label}{field.required && ' *'}</Label>
        <div className="flex gap-2">
          <input type="color" value={value || '#C52429'} onChange={(e) => onChange(e.target.value)} className="h-10 w-14 rounded border border-neutral-300 dark:border-neutral-700 cursor-pointer" />
          <Input value={value || ''} onChange={(e) => onChange(e.target.value)} className="font-mono" />
        </div>
      </div>
    );
  }
  if (field.type === 'number') {
    return (
      <div className={`space-y-1.5 ${cs}`}>
        <Label>{field.label}{field.required && ' *'}</Label>
        <Input type="number" required={field.required} value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />
      </div>
    );
  }
  return (
    <div className={`space-y-1.5 ${cs}`}>
      <Label>{field.label}{field.required && ' *'}</Label>
      <Input required={field.required} value={value || ''} onChange={(e) => onChange(e.target.value)} className={field.mono ? 'font-mono' : ''} />
    </div>
  );
}

export default function MasterDataPage() {
  const { key } = useParams();
  const config = MASTER_CONFIGS[key];

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!config) return;
    setLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      const { data } = await api.get(config.endpoint, { params });
      setItems(data.items); setTotal(data.total);
    } catch { toast.error('Erro ao carregar'); } finally { setLoading(false); }
  };

  useEffect(() => { setPage(1); setSearch(''); setStatusFilter('all'); }, [key]);
  useEffect(() => { load(); }, [key, page, statusFilter]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 350); return () => clearTimeout(t); }, [search]); // eslint-disable-line

  if (!config) return <Navigate to="/dashboard" replace />;

  const openCreate = () => { setEditing(null); setForm(config.defaultForm); setModalOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...config.defaultForm, ...item }); setModalOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) { await api.put(`${config.endpoint}/${editing.id}`, form); toast.success('Atualizado'); }
      else { await api.post(config.endpoint, form); toast.success('Criado'); }
      setModalOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao salvar'); } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`${config.endpoint}/${deleteId}`); toast.success('Excluído'); setDeleteId(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
  };

  const exportFile = async (fmt) => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (statusFilter !== 'all') params.set('status_filter', statusFilter);
      const resp = await api.get(`${config.endpoint}/export/${fmt}?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${key}.${fmt === 'xlsx' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao exportar'); }
  };

  const action = (
    <div className="flex gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline"><Download className="h-4 w-4 mr-2" />Exportar</Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => exportFile('csv')}><FileSpreadsheet className="h-4 w-4 mr-2" />CSV</DropdownMenuItem>
          <DropdownMenuItem onClick={() => exportFile('xlsx')}><FileSpreadsheet className="h-4 w-4 mr-2" />Excel (XLSX)</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button onClick={openCreate} className="bg-brand hover:bg-[#A81E23] text-white">+ {config.actionLabel || 'Novo'}</Button>
    </div>
  );

  return (
    <div>
      <PageHeader title={config.title} subtitle={config.subtitle} action={action} />

      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pesquisar..." className="pl-9" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-40"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="active">Ativos</SelectItem>
              <SelectItem value="inactive">Inativos</SelectItem>
            </SelectContent>
          </Select>
          {(search || statusFilter !== 'all') && (
            <Button variant="outline" onClick={() => { setSearch(''); setStatusFilter('all'); }}><X className="h-4 w-4 mr-1" />Limpar</Button>
          )}
        </div>
      </div>

      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase tracking-wider">
              <tr>
                {config.columns.map(c => <th key={c.key} className="text-left px-4 py-3 font-medium">{c.label}</th>)}
                <th className="text-right px-4 py-3 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {loading ? (
                <tr><td colSpan={config.columns.length + 1} className="p-4"><TableSkeleton rows={5} cols={config.columns.length + 1} /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={config.columns.length + 1}><EmptyState icon={config.icon} title="Nenhum registro encontrado" description="Ajuste os filtros ou adicione um novo registro." /></td></tr>
              ) : items.map((item) => (
                <tr key={item.id} className="tr-hover">
                  {config.columns.map(col => <td key={col.key} className="px-4 py-3">{renderCell(item, col)}</td>)}
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(item)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(item.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} total={total} limit={limit} onPageChange={setPage} />
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Editar ${config.title}` : `Novo ${config.actionLabel?.replace('Novo ', '').replace('Nova ', '') || 'Registro'}`}</DialogTitle>
            <DialogDescription>Preencha os dados abaixo.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {config.fields.map((f) => (
              <FieldRenderer key={f.key} field={f} value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} />
            ))}
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-brand hover:bg-[#A81E23] text-white">{saving ? 'Salvando...' : 'Salvar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir registro?</AlertDialogTitle>
            <AlertDialogDescription>Soft delete. O registro será marcado como excluído.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
